import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import DatePicker from "react-datepicker"
import { Modal } from 'react-bootstrap'

import { httpOk, GET, POST } from 'helpers/api'
import { formatDate } from 'helpers/helpers'
import GeofenceModal from './GeofenceModal'

const IMG_BASE = process.env.NODE_ENV === 'production'
	? (process.env.REACT_APP_API_URL || 'https://backend.tameer.uz/api').replace(/\/api$/, '')
	: 'http://127.0.0.1:8000'

const WEEKDAYS = [
	{ n: 1, label: 'Dushanba' },
	{ n: 2, label: 'Seshanba' },
	{ n: 3, label: 'Chorshanba' },
	{ n: 4, label: 'Payshanba' },
	{ n: 5, label: 'Juma' },
	{ n: 6, label: 'Shanba' },
	{ n: 0, label: 'Yakshanba' },
]

// Ikki vaqt orasidagi davomiylik "HH:MM"
function duration(inAt, outAt) {
	if (!inAt || !outAt) return '--:--'
	const ms = new Date(outAt.replace(' ', 'T')) - new Date(inAt.replace(' ', 'T'))
	if (ms <= 0) return '--:--'
	const mins = Math.floor(ms / 60000)
	return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`
}
const timeOnly = (dt) => dt ? formatDate(dt, 'HH:mm') : '--:--'

function StatCard({ label, value, color, bg, icon }) {
	return (
		<div className="col-6 col-lg-3 mb-3">
			<div className="card h-100" style={{ background: bg, border: 'none' }}>
				<div className="card-body d-flex justify-content-between align-items-center">
					<div>
						<div className="small" style={{ color }}>{label}</div>
						<div className="fs-3 fw-bold" style={{ color }}>{value}</div>
					</div>
					<span className="d-flex align-items-center justify-content-center"
						style={{ width: 42, height: 42, borderRadius: '50%', background: '#fff' }}>
						<i className={icon} style={{ color, fontSize: 20 }}></i>
					</span>
				</div>
			</div>
		</div>
	)
}

function Attendance() {
	const { t } = useTranslation()

	const [tab, setTab] = useState('daily')
	const [departments, setDepartments] = useState([])
	const [departmentId, setDepartmentId] = useState(null)

	const [date, setDate] = useState(new Date())
	const [rows, setRows] = useState([])

	const [month, setMonth] = useState(formatDate(new Date(), 'yyyy-MM'))
	const [monthly, setMonthly] = useState(null)

	const [showManual, setShowManual] = useState(false)
	const [manual, setManual] = useState({ employee_id: null, work_date: new Date(), check_in: '', check_out: '', note: '' })

	const [restDays, setRestDays] = useState([0])
	const [restModal, setRestModal] = useState({ show: false, days: [0] })
	const [showGeofence, setShowGeofence] = useState(false)

	async function loadRestDays() {
		const res = await GET('/attendance/rest-days')
		if (httpOk(res)) setRestDays(res.data.rest_days || [0])
	}

	async function saveRestDays() {
		const res = await POST('/attendance/rest-days', { rest_days: restModal.days }, { loader: true })
		if (httpOk(res)) {
			setRestDays(restModal.days)
			setRestModal({ ...restModal, show: false })
		}
	}

	function toggleRestDay(n) {
		const days = restModal.days.includes(n)
			? restModal.days.filter(x => x !== n)
			: [...restModal.days, n]
		setRestModal({ ...restModal, days })
	}

	async function loadDepartments() {
		const res = await GET('/helper/departments')
		if (httpOk(res)) setDepartments(res.data)
	}

	async function loadDaily() {
		const params = { date: formatDate(date, 'yyyy-MM-dd') }
		if (departmentId) params.department_id = departmentId
		const res = await GET('/attendance', params, { loader: true })
		if (httpOk(res)) setRows(res.data.data)
	}

	async function loadMonthly() {
		const params = { month }
		if (departmentId) params.department_id = departmentId
		const res = await GET('/attendance/monthly', params, { loader: true })
		if (httpOk(res)) setMonthly(res.data)
	}

	// Mavjud xodimni bosganda — kirish/chiqish vaqti to'ldirilган holda ochamiz
	function openEdit(row) {
		setManual({
			employee_id: row.id,
			work_date: date,
			check_in: row.check_in_at ? formatDate(row.check_in_at, 'HH:mm') : '',
			check_out: row.check_out_at ? formatDate(row.check_out_at, 'HH:mm') : '',
			note: row.note || '',
		})
		setShowManual(true)
	}

	async function saveManual(e) {
		e.preventDefault()
		const wd = formatDate(manual.work_date, 'yyyy-MM-dd')
		const res = await POST('/attendance', {
			employee_id: manual.employee_id,
			work_date: wd,
			check_in_at: manual.check_in ? `${wd} ${manual.check_in}:00` : null,
			check_out_at: manual.check_out ? `${wd} ${manual.check_out}:00` : null,
			status: 'present',
			note: manual.note,
		}, { loader: true })
		if (httpOk(res)) {
			setShowManual(false)
			setManual({ employee_id: null, work_date: date, check_in: '', check_out: '', note: '' })
			loadDaily()
		}
	}

	useEffect(() => { loadDepartments(); loadRestDays() }, [])

	useEffect(() => {
		if (tab === 'daily') loadDaily()
		else loadMonthly()
	}, [tab, date, month, departmentId]) // eslint-disable-line react-hooks/exhaustive-deps

	// Statistika
	const total = rows.length
	const atWork = rows.filter(r => ['present', 'late', 'half'].includes(r.status) || r.check_in_at).length
	const late = rows.filter(r => r.status === 'late').length
	const absent = total - atWork

	// Oylik grid
	const monthMap = {}
	if (monthly) {
		monthly.attendances.forEach(a => {
			const day = Number(String(a.work_date).slice(8, 10))
			monthMap[`${a.employee_id}_${day}`] = a.status
		})
	}
	const days = monthly ? Array.from({ length: monthly.days_in_month }, (_, i) => i + 1) : []

	function isWeekend(d) {
		const [y, m] = month.split('-')
		const dow = new Date(Number(y), Number(m) - 1, d).getDay()
		return restDays.includes(dow)
	}

	return (
		<>
			<div className="page-header d-flex justify-content-between align-items-center flex-wrap gap-2">
				<div className="d-flex align-items-center gap-3">
					<h4 className="mb-0">Davomat</h4>
					<div className="btn-group">
						<button className={`btn btn-sm ${tab === 'daily' ? 'btn-primary' : 'btn-outline-primary'}`}
							onClick={() => setTab('daily')}>Kunlik</button>
						<button className={`btn btn-sm ${tab === 'monthly' ? 'btn-primary' : 'btn-outline-primary'}`}
							onClick={() => setTab('monthly')}>Oylik</button>
					</div>
				</div>
				<div className="d-flex gap-2 flex-wrap">
					{tab === 'daily' ? (
						<div style={{ minWidth: '150px' }}>
							<DatePicker className="form-control" selected={date}
								onChange={(d) => setDate(d)} dateFormat="dd.MM.yyyy" maxDate={new Date()} />
						</div>
					) : (
						<div style={{ minWidth: '150px' }}>
							<DatePicker className="form-control" selected={new Date(month + '-01')}
								onChange={(d) => setMonth(formatDate(d, 'yyyy-MM'))}
								dateFormat="MM.yyyy" showMonthYearPicker />
						</div>
					)}
					<div style={{ minWidth: '180px' }}>
						<Select isClearable options={departments}
							value={departments.find(o => o.id === departmentId) || null}
							onChange={(opt) => setDepartmentId(opt ? opt.id : null)}
							placeholder="Barcha bo'limlar"
							getOptionLabel={(o) => o.name} getOptionValue={(o) => o.id} />
					</div>
					{tab === 'daily' &&
						<button className="btn btn-outline-primary"
							onClick={() => { setManual({ employee_id: null, work_date: date, check_in: '', check_out: '', note: '' }); setShowManual(true) }}>
							<i className="uil-plus me-1"></i>Qo'lda davomat
						</button>
					}
					{tab === 'monthly' &&
						<button className="btn btn-outline-secondary"
							onClick={() => setRestModal({ show: true, days: [...restDays] })}>
							<i className="uil-calender me-1"></i>Dam olish kunlari
						</button>
					}
					<button className="btn btn-outline-secondary" onClick={() => setShowGeofence(true)}>
						<i className="uil-map-marker me-1"></i>Ishxona joylashuvi
					</button>
				</div>
			</div>

			{tab === 'daily' &&
				<>
					{/* STATISTIKA */}
					<div className="row">
						<StatCard label="Barchasi" value={total} color="#0f9d58" bg="#e6f7ee" icon="uil-users-alt" />
						<StatCard label="Ishda" value={atWork} color="#1a73e8" bg="#e8f0fe" icon="uil-check-circle" />
						<StatCard label="Kech" value={late} color="#b8860b" bg="#fef7e0" icon="uil-clock" />
						<StatCard label="Ishda emas" value={absent} color="#d93025" bg="#fce8e6" icon="uil-times-circle" />
					</div>

					<div className="card">
						<div className="card-body">
							<h6 className="fw-bold mb-3">Xodimlar davomati</h6>
							<div className="table-responsive">
								<table className="table table-borderless mb-0 align-middle">
									<thead>
										<tr className="text-muted small border-bottom">
											<th>Xodim</th>
											<th className="text-center">Kirish</th>
											<th className="text-center">Chiqish</th>
											<th className="text-center">Qo'shimcha</th>
											<th className="text-center">Jami</th>
											<th>Bo'lim</th>
										</tr>
									</thead>
									<tbody>
										{rows.map((r) => (
											<tr key={r.id} className="border-bottom cursor" onClick={() => openEdit(r)}>
												<td>
													<div className="d-flex align-items-center gap-2">
														{r.photo
															? <img src={`${IMG_BASE}/uploads/employees/${r.photo}`} alt=""
																style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover' }} />
															: <span className="d-flex align-items-center justify-content-center"
																style={{ width: 38, height: 38, borderRadius: '50%', background: '#e9ecef', fontSize: 13 }}>
																{r.name?.[0]}
															</span>}
														<div>
															<div className="fw-semibold">{r.name}</div>
															<div className="text-muted small">{r.department_name || '—'}</div>
														</div>
													</div>
												</td>
												<td className="text-center fw-semibold">{timeOnly(r.check_in_at)}</td>
												<td className="text-center fw-semibold">{timeOnly(r.check_out_at)}</td>
												<td className="text-center text-muted">--:--</td>
												<td className="text-center fw-bold">{duration(r.check_in_at, r.check_out_at)}</td>
												<td className="text-muted">{r.department_name}</td>
											</tr>
										))}
										{rows.length === 0 &&
											<tr><td colSpan={6} className="text-center text-muted py-4">Xodim yo'q</td></tr>
										}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</>
			}

			{/* OYLIK GRID */}
			{tab === 'monthly' && monthly &&
				<div className="card">
					<div className="card-body">
						<div className="table-responsive" style={{ maxHeight: '75vh' }}>
							<table className="table mb-0 text-center" style={{ fontSize: 12, borderCollapse: 'separate', borderSpacing: 0 }}>
								<thead>
									<tr>
										<th style={{ position: 'sticky', left: 0, top: 0, zIndex: 3, background: '#f8f9fa', minWidth: 170, textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Xodim</th>
										{days.map(d => {
											const w = isWeekend(d)
											return <th key={d} style={{ position: 'sticky', top: 0, minWidth: 30, padding: '6px 2px', background: w ? '#fdecea' : '#f8f9fa', color: w ? '#dc3545' : '#6c757d', borderBottom: '2px solid #dee2e6' }}>{d}</th>
										})}
										<th style={{ position: 'sticky', top: 0, minWidth: 55, background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>Jami</th>
									</tr>
								</thead>
								<tbody>
									{monthly.employees.map(e => {
										const presentCount = days.filter(d => monthMap[`${e.id}_${d}`]).length
										return (
											<tr key={e.id}>
												<td className="fw-semibold text-nowrap" style={{ position: 'sticky', left: 0, background: '#fff', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>{e.name}</td>
												{days.map(d => {
													const present = !!monthMap[`${e.id}_${d}`]
													const w = isWeekend(d)
													return (
														<td key={d} style={{ padding: 0, height: 30, borderBottom: '1px solid #f0f0f0', background: present ? '#e6f7ee' : (w ? '#fdf4f3' : '#fff') }}>
															{present && <i className="uil-check" style={{ color: '#198754', fontSize: 15 }}></i>}
														</td>
													)
												})}
												<td className="fw-bold" style={{ background: '#f8f9fa', borderBottom: '1px solid #f0f0f0' }}>{presentCount}</td>
											</tr>
										)
									})}
									{monthly.employees.length === 0 &&
										<tr><td colSpan={days.length + 2} className="text-center text-muted py-3">Xodim yo'q</td></tr>
									}
								</tbody>
							</table>
						</div>
						<p className="text-muted small mt-2 mb-0">
							<span style={{ display: 'inline-block', width: 12, height: 12, background: '#e6f7ee', border: '1px solid #198754', borderRadius: 2 }}></span> — kelgan kun ·
							<span className="ms-1" style={{ color: '#dc3545' }}>qizil</span> ustunlar — dam olish
						</p>
					</div>
				</div>
			}

			<GeofenceModal show={showGeofence} onClose={() => setShowGeofence(false)} />

			{/* DAM OLISH KUNLARI MODAL */}
			<Modal show={restModal.show} animation={false} centered backdrop="static"
				onHide={() => setRestModal({ ...restModal, show: false })}>
				<Modal.Header closeButton>
					<Modal.Title>Dam olish kunlari</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="text-muted small">Korxonangizning dam olish kunlarini belgilang — jadvalda qizil bilan ajratiladi.</p>
					<div className="d-flex flex-column gap-1">
						{WEEKDAYS.map(w => (
							<label key={w.n} className="d-flex align-items-center justify-content-between p-2 rounded cursor"
								style={{ border: '1px solid #dee2e6' }}>
								<span>{w.label}</span>
								<input type="checkbox" className="form-check-input"
									checked={restModal.days.includes(w.n)}
									onChange={() => toggleRestDay(w.n)} />
							</label>
						))}
					</div>
					<div className="d-flex gap-2 mt-3">
						<button type="button" className="btn btn-outline-warning w-100"
							onClick={() => setRestModal({ ...restModal, show: false })}>{t('cancel')}</button>
						<button type="button" className="btn btn-primary w-100" onClick={saveRestDays}>{t('save')}</button>
					</div>
				</Modal.Body>
			</Modal>

			{/* QO'LDA DAVOMAT MODAL */}
			<Modal show={showManual} animation={false} centered backdrop="static" onHide={() => setShowManual(false)}>
				<Modal.Header closeButton>
					<Modal.Title>Qo'lda davomat kiritish</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={saveManual} autoComplete="off">
						<div className="form-group">
							<label>Xodim<span className="required-mark">*</span></label>
							<Select
								options={rows}
								value={rows.find(r => r.id === manual.employee_id) || null}
								onChange={(opt) => setManual({ ...manual, employee_id: opt?.id })}
								placeholder="Xodim tanlang"
								getOptionLabel={(o) => o.name} getOptionValue={(o) => o.id} />
						</div>
						<div className="form-group">
							<label>Kun<span className="required-mark">*</span></label>
							<DatePicker className="form-control" selected={manual.work_date}
								onChange={(d) => setManual({ ...manual, work_date: d })}
								dateFormat="dd.MM.yyyy" maxDate={new Date()} />
						</div>
						<div className="row">
							<div className="col-6">
								<div className="form-group">
									<label>Kirish vaqti</label>
									<input type="time" className="form-control" value={manual.check_in}
										onChange={(e) => setManual({ ...manual, check_in: e.target.value })} />
								</div>
							</div>
							<div className="col-6">
								<div className="form-group">
									<label>Chiqish vaqti</label>
									<input type="time" className="form-control" value={manual.check_out}
										onChange={(e) => setManual({ ...manual, check_out: e.target.value })} />
								</div>
							</div>
						</div>
						<div className="form-group mb-0">
							<label>{t('note')}</label>
							<input type="text" className="form-control" value={manual.note}
								onChange={(e) => setManual({ ...manual, note: e.target.value })} />
						</div>
						<div className="d-flex gap-2 mt-3">
							<button type="button" className="btn btn-outline-warning w-100"
								onClick={() => setShowManual(false)}>{t('cancel')}</button>
							<button type="submit" className="btn btn-primary w-100" disabled={!manual.employee_id}>{t('save')}</button>
						</div>
					</form>
				</Modal.Body>
			</Modal>
		</>
	)
}

export default Attendance
